'use client';

import { LogOut } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Row, Stack } from '@/components/layout';
import { Logo } from '@/components/logo';
import ThemeToggle from '@/components/theme-toggle';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useSidebarMenu } from '@/hooks/useSidebarMenu';

export function AppSidebar() {
    const { authStatus, logout } = useAuth();
    const { menu } = useSidebarMenu();
    const pathname = usePathname();
    const { setOpenAsDrawer, displayAsDrawer } = useSidebar();

    const userInitials = authStatus?.userName
        ?.split(' ')
        .map((name: string) => name[0])
        .join('');

    const handleLinkClick = () => {
        if (displayAsDrawer) {
            setOpenAsDrawer(false);
        }
    };

    // Don't render sidebar if user is not authenticated
    if (!authStatus?.isAuthenticated) {
        return null;
    }

    return (
        <Sidebar
            className="bg-sidebar flex h-full w-64 flex-col"
            collapsible="icon"
        >
            <SidebarHeader>
                <Link href="/" onClick={handleLinkClick}>
                    <Logo className="hover:bg-sidebar-accent p-2 transition-[background]" />
                </Link>
            </SidebarHeader>

            <SidebarContent>
                <Stack className="box-border w-full items-start p-2 group-data-[collapsible=icon]:items-center">
                    <SidebarMenu>
                        {menu
                            .filter((item) => item.visible)
                            .map((item) => {
                                const isActive = pathname === item.href;

                                return (
                                    <SidebarMenuItem key={item.label}>
                                        <SidebarMenuButton
                                            size="lg"
                                            asChild
                                            isActive={isActive}
                                        >
                                            <Link
                                                href={item.href}
                                                onClick={handleLinkClick}
                                            >
                                                <item.icon className="size-5!" />
                                                <span className="grow overflow-hidden text-sm leading-none font-semibold text-nowrap overflow-ellipsis group-data-[collapsible=icon]:hidden">
                                                    {item.label}
                                                </span>
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                );
                            })}
                    </SidebarMenu>
                </Stack>
            </SidebarContent>

            <SidebarFooter>
                <Row className="w-full items-center gap-2 rounded-md p-2 group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:items-center">
                    <Avatar className="bg-primary size-8 rounded-sm">
                        <AvatarFallback className="bg-primary text-primary-foreground rounded-lg font-sans text-sm leading-none font-semibold">
                            {userInitials}
                        </AvatarFallback>
                    </Avatar>
                    <Stack className="grow items-start justify-center gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
                        <p className="overflow-hidden text-sm font-semibold text-nowrap overflow-ellipsis">
                            {authStatus?.userName}
                        </p>
                        <p className="text-primary overflow-hidden text-xs font-normal text-nowrap overflow-ellipsis">
                            {/** Convert from camel case to space separated words */}
                            {authStatus?.roles
                                .map((role) =>
                                    role.replace(/([A-Z])/g, ' $1').trim(),
                                )
                                .join(' ')}
                        </p>
                    </Stack>
                </Row>

                <Stack className="w-full items-start px-0 py-2 group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:items-center">
                    <SidebarMenu className="gap-1">
                        {displayAsDrawer && (
                            <SidebarMenuItem>
                                <ThemeToggle variant="button" />
                            </SidebarMenuItem>
                        )}
                        <SidebarMenuItem>
                            <SidebarMenuButton size="lg" onClick={logout}>
                                <LogOut className="size-4" />
                                <span className="grow text-sm leading-none font-normal text-nowrap overflow-ellipsis group-data-[collapsible=icon]:hidden">
                                    Logout
                                </span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </Stack>
            </SidebarFooter>
        </Sidebar>
    );
}
